import React, { useState } from 'react';

import { Add } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { findSimilarRoomAssets } from './roomNameMatch';
import type { RoomAssetMatch } from './roomNameMatch';
import type { RoomAssetLink } from './types';

type LinkRoomToAssetDialogProps = {
  assets: Array<RoomAssetLink>;
  onAddRoom: (asset: RoomAssetLink) => void;
  onClose: () => void;
  onCreateAsset: (asset: RoomAssetLink) => void;
  open: boolean;
  takenAssetKeys: Set<string>;
};

export default function LinkRoomToAssetDialog({
  assets,
  onAddRoom,
  onClose,
  onCreateAsset,
  open,
  takenAssetKeys,
}: LinkRoomToAssetDialogProps) {
  const [selectedKey, setSelectedKey] = useState('');
  const [mode, setMode] = useState<'link' | 'create'>('link');
  const [newAssetName, setNewAssetName] = useState('');
  const [similarMatches, setSimilarMatches] = useState<Array<RoomAssetMatch>>([]);
  const [selectedSimilarKey, setSelectedSimilarKey] = useState('');

  const allSimilarAssetsTaken = similarMatches.every((match) => takenAssetKeys.has(match.asset.id));
  const exactMatch = similarMatches.some((match) => match.similarity === 1);

  function handleAddRoom() {
    const asset = assets.find((candidate) => candidate.id === selectedKey);
    if (asset) {
      onAddRoom(asset);
    }
    onClose();
  }

  function handleCreateAsset() {
    const name = newAssetName.trim();
    const matches = findSimilarRoomAssets(name, assets);
    if (matches.length > 0) {
      const selectable = matches.find((match) => !takenAssetKeys.has(match.asset.id));
      setSimilarMatches(matches);
      setSelectedSimilarKey(selectable ? selectable.asset.id : '');
      return;
    }
    createAsset(name);
  }

  function createAsset(name: string) {
    const asset = { id: crypto.randomUUID(), name };
    onCreateAsset(asset);
    onAddRoom(asset);
    onClose();
  }

  function handleUseExistingAsset() {
    const match = similarMatches.find(({ asset }) => asset.id === selectedSimilarKey);
    if (match) {
      onAddRoom(match.asset);
    }
    onClose();
  }

  function resetState() {
    setSelectedKey('');
    setMode('link');
    setNewAssetName('');
    setSimilarMatches([]);
    setSelectedSimilarKey('');
  }

  if (similarMatches.length > 0) {
    return (
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={onClose}
        open={open}
        slotProps={{ transition: { onExited: resetState } }}
      >
        <DialogTitle>Is this the same room?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You entered <strong>{newAssetName.trim()}</strong>. This{' '}
            {exactMatch ? 'is an exact match for' : 'looks similar to'}{' '}
            {similarMatches.length === 1 ? 'a room' : 'rooms'} that already{' '}
            {similarMatches.length === 1 ? 'exists' : 'exist'} on this account.
          </Typography>
          <RadioGroup
            onChange={(e) => setSelectedSimilarKey(e.target.value)}
            sx={{ flexWrap: 'nowrap', gap: 1, mt: 2 }}
            value={selectedSimilarKey}
          >
            {similarMatches.map(({ asset }) => {
              const taken = takenAssetKeys.has(asset.id);
              return (
                <FormControlLabel
                  control={<Radio />}
                  disabled={taken}
                  key={asset.id}
                  label={asset.name}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, m: 0, px: 1.5, py: 1 }}
                  value={asset.id}
                />
              );
            })}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimilarMatches([])} variant="text">
            Back
          </Button>
          <Button onClick={() => createAsset(newAssetName.trim())} variant="text">
            No, create new
          </Button>
          {!allSimilarAssetsTaken && (
            <Button disabled={!selectedSimilarKey} onClick={handleUseExistingAsset} variant="contained">
              Yes, use existing
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  if (mode === 'create') {
    return (
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={onClose}
        open={open}
        slotProps={{ transition: { onExited: resetState } }}
      >
        <DialogTitle>Create new asset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            onChange={(e) => setNewAssetName(e.target.value)}
            sx={{ mt: 1 }}
            value={newAssetName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMode('link')} variant="text">
            Back
          </Button>
          <Button disabled={!newAssetName.trim()} onClick={handleCreateAsset} variant="contained">
            Create and add room
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open} slotProps={{ transition: { onExited: resetState } }}>
      <DialogTitle>Link room to asset</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" variant="body2">
          Select an existing asset, or create a new one.
        </Typography>

        {assets.length === 0 ? (
          <Typography color="text.secondary" mt={2} variant="body2">
            No assets yet — create one below.
          </Typography>
        ) : (
          <RadioGroup
            onChange={(e) => setSelectedKey(e.target.value)}
            sx={{ flexWrap: 'nowrap', gap: 1, maxHeight: 320, mt: 2, overflowY: 'auto' }}
            value={selectedKey}
          >
            {assets.map((asset) => {
              const taken = takenAssetKeys.has(asset.id);
              return (
                <FormControlLabel
                  control={<Radio />}
                  disabled={taken}
                  key={asset.id}
                  label={asset.name}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, m: 0, px: 1.5, py: 1 }}
                  value={asset.id}
                />
              );
            })}
          </RadioGroup>
        )}

        <Button fullWidth onClick={() => setMode('create')} startIcon={<Add />} sx={{ mt: 1.5 }} variant="outlined">
          Create new asset
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button disabled={!selectedKey} onClick={handleAddRoom} variant="contained">
          Add room
        </Button>
      </DialogActions>
    </Dialog>
  );
}
